/**
 * Candidate inbox — verified employers reach out through Resmo without
 * seeing the candidate's email until the candidate accepts.
 *
 * Each row expands into an Accept + Decline pair. Accept lets the candidate
 * ship a short reply back to the employer through the same relay channel;
 * decline closes the thread. Once resolved, the candidate can still see the
 * message and their reply for reference.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Mail, ChevronDown, ChevronUp, CheckCircle2, XCircle, Loader, Inbox, Briefcase, Clock, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";

export default function CandidateRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/candidate/contact-requests");
      setRows(r.data.requests || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
    setReply("");
  };

  const respond = async (req, action) => {
    setBusy(req.id + ":" + action);
    try {
      await api.post(`/candidate/contact-requests/${req.id}/respond`,
        { action, reply: reply.trim() });
      setExpandedId(null);
      setReply("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't send response. Please try again.");
    } finally { setBusy(null); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const resolved = rows.filter((r) => r.status !== "pending");

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-5">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest"
               style={{ color: "#FF6A00", fontWeight: 700 }}>
            · Talent Directory · Inbox
          </div>
          <h1 className="font-display uppercase leading-tight"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
            Employers who want to talk
          </h1>
          <p className="text-sm leading-relaxed mt-1" style={{ color: "#334155" }}>
            Verified employers reach out through Resmo. Your email stays private until you accept.
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <Loader className="mx-auto animate-spin" size={22} style={{ color: "#FF6A00" }} />
            <div className="mt-2 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              · Loading
            </div>
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-12 rounded-md"
               style={{ background: "white", border: "1.5px dashed #CBD5E1" }}
               data-testid="candidate-requests-empty">
            <Inbox size={26} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              · Inbox zero
            </div>
            <div className="text-[13px] mt-1" style={{ color: "#475569" }}>
              No employer requests yet.{" "}
              <Link to="/studio" style={{ color: "#FF6A00", textDecoration: "underline", fontWeight: 700 }}>
                Publish a résumé to the Talent Directory →
              </Link>
            </div>
          </div>
        )}

        {pending.length > 0 && (
          <SectionHeader title={`New · ${pending.length}`} accent="#FF6A00" />
        )}
        <div className="space-y-2" data-testid="candidate-requests-pending">
          {pending.map((r) => (
            <RequestCard key={r.id}
                         req={r}
                         expanded={expandedId === r.id}
                         onToggle={() => toggle(r.id)}
                         reply={reply}
                         onReplyChange={setReply}
                         onRespond={respond}
                         busy={busy} />
          ))}
        </div>

        {resolved.length > 0 && (
          <>
            <SectionHeader title={`Resolved · ${resolved.length}`} accent="#94A3B8" />
            <div className="space-y-2" data-testid="candidate-requests-resolved">
              {resolved.map((r) => (
                <RequestCard key={r.id}
                             req={r}
                             expanded={expandedId === r.id}
                             onToggle={() => toggle(r.id)}
                             reply={reply}
                             onReplyChange={setReply}
                             onRespond={respond}
                             busy={busy} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, accent }) {
  return (
    <div className="mt-6 mb-2 font-mono-rm text-[10px] uppercase tracking-widest"
         style={{ color: accent, fontWeight: 700, letterSpacing: "0.14em" }}>
      · {title}
    </div>
  );
}

function RequestCard({ req, expanded, onToggle, reply, onReplyChange, onRespond, busy }) {
  const isPending = req.status === "pending";
  return (
    <div className="rounded-md overflow-hidden"
         style={{
           background: "white",
           border: isPending ? "2px solid #0F1B2D" : "1.5px solid #CBD5E1",
           boxShadow: isPending ? "3px 3px 0 0 #FFC145" : "none",
         }}
         data-testid={`candidate-request-${req.id}`}>
      <button type="button" onClick={onToggle}
              className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left"
              data-testid={`candidate-request-toggle-${req.id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={req.status} />
            <span className="font-display uppercase leading-tight"
                  style={{ fontSize: "0.98rem", color: "#0F1B2D", letterSpacing: "-0.01em" }}>
              {req.employer_first} {req.employer_last}
            </span>
            <span className="text-[12px]" style={{ color: "#475569" }}>
              · {req.employer_company}
            </span>
          </div>
          <div className="text-[13px] mt-1" style={{ color: "#334155" }}>
            <Briefcase size={12} className="inline mr-1" style={{ color: "#FF6A00" }} />
            <b>{req.role_title}</b>
          </div>
          <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>
            <Clock size={10} className="inline mr-1" />
            {new Date(req.created_at).toLocaleDateString()}
          </div>
        </div>
        {expanded ? <ChevronUp size={16} style={{ color: "#0F1B2D" }} />
                  : <ChevronDown size={16} style={{ color: "#0F1B2D" }} />}
      </button>
      {expanded && (
        <div className="px-4 pb-4" style={{ borderTop: "1px dashed #CBD5E1" }}
             data-testid={`candidate-request-body-${req.id}`}>
          <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-3 mb-1"
               style={{ color: "#94A3B8", fontWeight: 700 }}>
            · The message
          </div>
          <div className="text-[13px] leading-relaxed whitespace-pre-wrap"
               style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1",
                        padding: "10px 12px", borderRadius: 6, color: "#334155" }}
               data-testid={`candidate-request-message-${req.id}`}>
            {req.message}
          </div>

          {req.candidate_reply && (
            <>
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-3 mb-1"
                   style={{ color: "#94A3B8", fontWeight: 700 }}>
                · Your reply
              </div>
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap"
                   style={{ background: "#FEF3C7", border: "1px dashed #FFC145",
                            padding: "10px 12px", borderRadius: 6, color: "#334155" }}>
                {req.candidate_reply}
              </div>
            </>
          )}

          {isPending && (
            <>
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-3 mb-1"
                   style={{ color: "#94A3B8", fontWeight: 700 }}>
                · Optional reply
              </div>
              <textarea value={reply} onChange={(e) => onReplyChange(e.target.value)}
                        rows={3} placeholder="Thanks — happy to chat. Free Wed 2-3 PM MT."
                        className="w-full rounded px-2.5 py-2 text-[13px]"
                        style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC", resize: "vertical" }}
                        data-testid={`candidate-request-reply-${req.id}`} />
              <div className="flex flex-wrap items-center gap-2 mt-3 justify-end">
                <button onClick={() => onRespond(req, "decline")}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-[11px] font-mono-rm uppercase tracking-widest"
                        style={{ background: "white", color: "#B91C1C",
                                 border: "1.5px solid #B91C1C", fontWeight: 700,
                                 cursor: busy ? "wait" : "pointer" }}
                        data-testid={`candidate-request-decline-${req.id}`}>
                  <XCircle size={12} /> Decline
                </button>
                <button onClick={() => onRespond(req, "accept")}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-[11px] font-mono-rm uppercase tracking-widest"
                        style={{ background: "#0E5A4A", color: "white",
                                 border: "1.5px solid #0E5A4A", fontWeight: 700,
                                 boxShadow: "2px 2px 0 0 #0F1B2D",
                                 cursor: busy ? "wait" : "pointer" }}
                        data-testid={`candidate-request-accept-${req.id}`}>
                  {busy === (req.id + ":accept") ? <Loader size={12} className="animate-spin" />
                                                : <CheckCircle2 size={12} />}
                  Accept · reveal my email
                </button>
              </div>
              <div className="text-[11px] mt-2 leading-snug" style={{ color: "#94A3B8" }}>
                <Sparkles size={10} className="inline mr-1" style={{ color: "#FFC145" }} />
                <Mail size={10} className="inline mr-1" style={{ color: "#0F1B2D" }} />
                Accepting sends your email + reply directly to {req.employer_first}.
                Declining is silent — the employer just sees &quot;not a fit right now.&quot;
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = ({
    pending:  { bg: "#FFF4E0", color: "#FF6A00", label: "New" },
    accepted: { bg: "#D1FAE5", color: "#0E5A4A", label: "Accepted" },
    declined: { bg: "#FEE2E2", color: "#B91C1C", label: "Declined" },
    expired:  { bg: "#F1F5F9", color: "#64748B", label: "Expired" },
  }[status] || { bg: "#F1F5F9", color: "#64748B", label: status });
  return (
    <span className="text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
          style={{ background: meta.bg, color: meta.color, fontWeight: 700 }}>
      {meta.label}
    </span>
  );
}
